<template>
    <div class="music-phrase-container">
        <div class="phrase-display">
            <!-- Music staff -->
            <div ref="vexContainer" class="vex-container"></div>

            <!-- Fingering diagrams positioned above/below notes -->
            <div v-if="showFingerings" class="fingerings-container">
                <div
                    v-for="(noteInfo, index) in notesWithPositions"
                    :key="index"
                    v-show="noteInfo.showFingering"
                    class="fingering-wrapper"
                    :style="{
                        left: noteInfo.x + 'px',
                        bottom: '0px',
                    }"
                >
                    <FluteFingering
                        :note="showFingeringLabels ? noteInfo.note : undefined"
                        :fingering="noteInfo.fingering"
                        :width="fingeringWidth"
                        :height="fingeringHeight"
                        orientation="vertical"
                    />
                </div>
            </div>

            <div v-if="error" class="error-message">{{ error }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick, computed } from "vue";
import {
    Renderer,
    Stave,
    StaveNote,
    Voice,
    Formatter,
    Accidental,
    Stem,
} from "vexflow";
import FluteFingering from "./FluteFingering.vue";
import {
    getStandardFingering,
    getFingeringByMidi,
    midiToNote,
    type FingeringState,
} from "../data/fluteFingeringChart";
import { type RawNote } from "../pitch/types";
import {
    midiToNoteName,
    shouldPreferFlat,
    getRequiredAccidental,
} from "../pitch/notationUtils";

/**
 * Note data for MusicPhrase component
 * Extends RawNote (MIDI + spelling) with visual/interactive properties
 */
export interface NoteData extends RawNote {
    duration?: string; // e.g., "quarter", "half", "eighth"
    fingering?: FingeringState; // Optional custom fingering
    color?: string; // Custom note color (e.g., "#ff0000", "red")
    opacity?: number; // Note opacity 0-1 (for muted/past notes)
    highlight?: boolean; // Highlight this note (e.g., current note being played)
    incorrect?: boolean; // Mark as incorrect (for feedback)
    sustaining?: boolean; // Visual feedback that note is being held (shimmer effect)
}

/**
 * Overlay configuration for showing a played note on top of a goal note
 * Used for real-time pitch feedback
 */
export interface NoteOverlay {
    noteIndex: number; // Index of the goal note to overlay on
    playedNote: RawNote; // The actual note played (midi + optional spelling)
    color?: string; // Color for overlay note (default: "#F44336" red)
}

interface Props {
    notes?: NoteData[];
    clef?: string;
    width?: number;
    height?: number;
    showFingerings?: boolean;
    showFingeringLabels?: boolean;
    skipRepeatingFingerings?: boolean; // Don't show fingering when note repeats
    fingeringWidth?: number;
    fingeringHeight?: number;
    timeSignature?: string; // e.g., "4/4", "3/4"
    keySignature?: string; // e.g., "C", "G", "D", "F", "Bb", "A"
    enforceTimeSignature?: boolean; // When false, renders notes without barlines in "free timing" mode
    overlay?: NoteOverlay; // Optional overlay for pitch feedback
}

const props = withDefaults(defineProps<Props>(), {
    notes: () => [
        { midi: 72, duration: "quarter" }, // C5
        { midi: 74, duration: "quarter" }, // D5
        { midi: 76, duration: "quarter" }, // E5
        { midi: 77, duration: "quarter" }, // F5
    ],
    clef: "treble",
    width: 800,
    height: 275,
    showFingerings: true,
    showFingeringLabels: false,
    skipRepeatingFingerings: true,
    fingeringWidth: 110,
    fingeringHeight: 120,
    timeSignature: "4/4",
    keySignature: undefined,
    enforceTimeSignature: true,
});

const vexContainer = ref<HTMLDivElement>();
const error = ref<string>("");
const notesWithPositions = ref<
    Array<{
        note: string;
        x: number;
        showFingering: boolean;
        fingering: FingeringState;
    }>
>([]);

// Map duration names to VexFlow duration values
const durationMap: Record<string, string> = {
    whole: "w",
    half: "h",
    quarter: "q",
    eighth: "8",
    sixteenth: "16",
    "thirty-second": "32",
    "sixty-fourth": "64",
};

// Map VexFlow durations to beat values (in quarter note beats)
const durationToBeatValue: Record<string, number> = {
    w: 4, // whole note = 4 beats
    h: 2, // half note = 2 beats
    q: 1, // quarter note = 1 beat
    "8": 0.5, // eighth note = 0.5 beats
    "16": 0.25, // sixteenth note = 0.25 beats
    "32": 0.125, // thirty-second note = 0.125 beats
    "64": 0.0625, // sixty-fourth note = 0.0625 beats
};

// Determine if note should have stem down (C5 and above)
function shouldStemDown(midiNumber: number): boolean {
    return midiNumber >= 72;
}

// Parse note string (e.g., "C4", "F#5", "Bb3") into VexFlow format
function parseNote(noteStr: string): { keys: string[]; accidentals: string[] } {
    if (!noteStr) {
        return { keys: ["C/4"], accidentals: [] };
    }

    const cleanNote = noteStr.replace(/\s/g, "").toUpperCase();
    const match = cleanNote.match(/^([A-G])([#Bb]?)([\d]+)$/);

    if (!match) {
        throw new Error(`Invalid note format: ${noteStr}`);
    }

    const [, letter, accidental, octave] = match;
    const key = `${letter}/${octave}`;
    const accidentals: string[] = [];

    if (accidental) {
        if (accidental === "#") {
            accidentals.push("#");
        } else if (accidental === "B" || accidental === "b") {
            accidentals.push("b");
        }
    }

    return { keys: [key], accidentals };
}

function getDuration(durationStr?: string): string {
    if (!durationStr) return "q";

    const mapped = durationMap[durationStr.toLowerCase()];
    if (mapped) return mapped;

    if (/^[whq]$|^[0-9]+$/.test(durationStr)) {
        return durationStr;
    }

    return "q";
}

// Calculate total duration of all notes in beats
function calculateTotalBeats(notes: NoteData[]): number {
    let totalBeats = 0;

    notes.forEach((noteData) => {
        const duration = getDuration(noteData.duration);
        const beatValue = durationToBeatValue[duration] || 1; // Default to 1 beat if unknown
        totalBeats += beatValue;
    });

    return totalBeats;
}

function shouldShowFingering(index: number, currentMidi: number): boolean {
    if (!props.skipRepeatingFingerings) {
        return true;
    }

    // Always show first note's fingering
    if (index === 0) {
        return true;
    }

    // Compare with previous note (MIDI numbers)
    const prevMidi = props.notes[index - 1]?.midi;
    return currentMidi !== prevMidi;
}

function applyNoteDecorations(staveNote: StaveNote, noteData: NoteData) {
    // Determine final color and opacity
    let fillColor = noteData.color || "black";
    let strokeColor = noteData.color || "black";
    let opacity = noteData.opacity !== undefined ? noteData.opacity : 1.0;

    // Highlight styling (current note being played)
    if (noteData.highlight) {
        fillColor = noteData.color || "#2196F3"; // Blue highlight
        strokeColor = fillColor;
        // Add glow effect for highlighted notes
        staveNote.setStyle({
            fillStyle: fillColor,
            strokeStyle: strokeColor,
            shadowColor: fillColor,
            shadowBlur: 10,
        });
        return;
    }

    // Sustaining styling (note being held down)
    if (noteData.sustaining) {
        fillColor = noteData.color || "#FF9800"; // Orange for sustaining
        strokeColor = fillColor;
        // Add subtle glow for sustaining notes
        staveNote.setStyle({
            fillStyle: fillColor,
            strokeStyle: strokeColor,
            shadowColor: fillColor,
            shadowBlur: 8,
        });
        return;
    }

    // Incorrect note styling
    if (noteData.incorrect) {
        fillColor = noteData.color || "#F44336"; // Red for incorrect
        strokeColor = fillColor;
        staveNote.setStyle({
            fillStyle: fillColor,
            strokeStyle: strokeColor,
        });
        return;
    }

    // Apply custom color and opacity
    if (noteData.color || opacity < 1.0) {
        const rgbaColor =
            opacity < 1.0 ? convertToRGBA(fillColor, opacity) : fillColor;

        staveNote.setStyle({
            fillStyle: rgbaColor,
            strokeStyle: rgbaColor,
        });
    }
}

function convertToRGBA(color: string, opacity: number): string {
    // Simple conversion - handles basic color names and hex
    // For hex colors
    if (color.startsWith("#")) {
        const hex = color.slice(1);
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // For named colors, use a simple map
    const colorMap: Record<string, string> = {
        black: "rgba(0, 0, 0, ",
        red: "rgba(255, 0, 0, ",
        blue: "rgba(0, 0, 255, ",
        green: "rgba(0, 128, 0, ",
        gray: "rgba(128, 128, 128, ",
        grey: "rgba(128, 128, 128, ",
    };

    const baseColor = colorMap[color.toLowerCase()] || "rgba(0, 0, 0, ";
    return baseColor + opacity + ")";
}

function renderNotation() {
    if (!vexContainer.value) return;

    try {
        error.value = "";

        // Validate time signature if enforcement is enabled
        if (props.enforceTimeSignature && props.timeSignature) {
            const [numBeats, beatValue] = props.timeSignature.split("/").map(Number);
            const totalBeats = calculateTotalBeats(props.notes);

            if (totalBeats !== numBeats) {
                vexContainer.value.innerHTML = "";
                error.value = `Time signature violation: ${props.timeSignature} requires ${numBeats} beats, but notes total ${totalBeats} beats`;
                return; // Don't render
            }
        }

        vexContainer.value.innerHTML = "";

        // Calculate auto-width if in free timing mode
        const calculatedWidth = !props.enforceTimeSignature
            ? Math.max(props.width, props.notes.length * 50 + 100) // 50px per note + padding
            : props.width;

        // Create VexFlow renderer
        const renderer = new Renderer(
            vexContainer.value,
            Renderer.Backends.SVG,
        );
        renderer.resize(calculatedWidth, props.height);
        const context = renderer.getContext();

        // Create stave
        const stave = new Stave(10, 40, calculatedWidth - 20);

        // Add clef
        const clefType = props.clef || "treble";
        if (["treble", "bass", "alto", "tenor"].includes(clefType)) {
            stave.addClef(clefType);
        } else {
            stave.addClef("treble");
        }

        // Add key signature if provided
        if (props.keySignature) {
            stave.addKeySignature(props.keySignature);
        }

        // Add time signature only if enforcing time signatures
        if (props.enforceTimeSignature && props.timeSignature) {
            stave.addTimeSignature(props.timeSignature);
        }

        // Draw stave
        stave.setContext(context).draw();

        // Create StaveNotes from the notes array
        const staveNotes: StaveNote[] = [];

        props.notes.forEach((noteData) => {
            // Convert MIDI to note name, respecting spelling preference
            const preferFlat =
                noteData.spelling === "flat" ||
                (noteData.spelling === undefined && shouldPreferFlat(props.keySignature));
            const noteName = midiToNoteName(noteData.midi, preferFlat);

            const { keys } = parseNote(noteName);
            const duration = getDuration(noteData.duration);

            const staveNote = new StaveNote({
                keys,
                duration,
                clef: props.clef || "treble",
                stemDirection: shouldStemDown(noteData.midi) ? Stem.DOWN : Stem.UP,
            });

            // Determine what accidental should be shown based on key signature
            const requiredAccidental = getRequiredAccidental(noteData.midi, noteName, props.keySignature);

            if (requiredAccidental) {
                // Apply the required accidental
                staveNote.addModifier(new Accidental(requiredAccidental), 0);
            }

            // Apply decorations
            applyNoteDecorations(staveNote, noteData);

            staveNotes.push(staveNote);
        });

        // Determine voice configuration based on enforce time signature setting
        let numBeats: number;
        let beatValue: number;

        if (props.enforceTimeSignature && props.timeSignature) {
            // Standard mode: use the provided time signature
            [numBeats, beatValue] = props.timeSignature.split("/").map(Number);
        } else {
            // Free timing mode: calculate total beats needed and use quarter note as beat unit
            const totalBeats = calculateTotalBeats(props.notes);
            numBeats = totalBeats;
            beatValue = 4; // Use quarter note as the beat unit
        }

        // Create voice and add notes
        const voice = new Voice({
            numBeats: numBeats,
            beatValue: beatValue,
        });
        voice.addTickables(staveNotes);

        // Format and draw
        const formatter = new Formatter();
        formatter.joinVoices([voice]).format([voice], calculatedWidth - 100);
        voice.draw(context, stave);

        // Render overlay note if provided (for pitch feedback)
        if (props.overlay && props.overlay.noteIndex >= 0 && props.overlay.noteIndex < props.notes.length) {
            const overlayIndex = props.overlay.noteIndex;
            const goalNoteData = props.notes[overlayIndex];
            const overlayColor = props.overlay.color || "#F44336"; // Default red

            // Convert overlay MIDI to note name
            const preferFlat =
                props.overlay.playedNote.spelling === "flat" ||
                (props.overlay.playedNote.spelling === undefined && shouldPreferFlat(props.keySignature));
            const overlayNoteName = midiToNoteName(props.overlay.playedNote.midi, preferFlat);

            const { keys: overlayKeys, accidentals: overlayAccidentals } = parseNote(overlayNoteName);
            const overlayDuration = getDuration(goalNoteData.duration);

            // Create overlay StaveNote
            const overlayStaveNote = new StaveNote({
                keys: overlayKeys,
                duration: overlayDuration,
                clef: props.clef || "treble",
                stemDirection: shouldStemDown(props.overlay.playedNote.midi) ? Stem.DOWN : Stem.UP,
            });

            // Add accidentals for overlay note
            const overlayRequiredAccidental = getRequiredAccidental(
                props.overlay.playedNote.midi,
                overlayNoteName,
                props.keySignature
            );
            if (overlayRequiredAccidental) {
                overlayStaveNote.addModifier(new Accidental(overlayRequiredAccidental), 0);
            }

            // Style overlay note with the specified color
            overlayStaveNote.setStyle({
                fillStyle: overlayColor,
                strokeStyle: overlayColor,
            });

            // Get the bounding box of the goal note to position overlay
            const goalStaveNote = staveNotes[overlayIndex];
            const goalBBox = goalStaveNote.getBoundingBox();

            // Create a single-note voice for the overlay
            const overlayVoice = new Voice({
                numBeats: numBeats,
                beatValue: beatValue,
            });

            // Add all notes to overlay voice, but make non-overlay notes invisible
            props.notes.forEach((noteData, index) => {
                if (index === overlayIndex) {
                    overlayVoice.addTickable(overlayStaveNote);
                } else {
                    // Create invisible placeholder notes
                    const preferFlat =
                        noteData.spelling === "flat" ||
                        (noteData.spelling === undefined && shouldPreferFlat(props.keySignature));
                    const noteName = midiToNoteName(noteData.midi, preferFlat);
                    const { keys } = parseNote(noteName);
                    const duration = getDuration(noteData.duration);

                    const placeholderNote = new StaveNote({
                        keys,
                        duration,
                        clef: props.clef || "treble",
                        stemDirection: shouldStemDown(noteData.midi) ? Stem.DOWN : Stem.UP,
                    });

                    // Make placeholder invisible
                    placeholderNote.setStyle({
                        fillStyle: "transparent",
                        strokeStyle: "transparent",
                    });

                    overlayVoice.addTickable(placeholderNote);
                }
            });

            // Format and draw overlay voice (this ensures proper positioning)
            const overlayFormatter = new Formatter();
            overlayFormatter.joinVoices([overlayVoice]).format([overlayVoice], calculatedWidth - 100);
            overlayVoice.draw(context, stave);

            // Hide ledger lines on transparent placeholder notes
            // VexFlow renders ledger lines with hardcoded stroke="#444" which overrides
            // the transparent strokeStyle on the parent <g>, so we need to manually hide them
            if (vexContainer.value) {
                const svg = vexContainer.value.querySelector('svg');
                if (svg) {
                    // Find all vf-stavenote groups that are transparent (placeholders)
                    const transparentGroups = svg.querySelectorAll('g.vf-stavenote[stroke="transparent"]');
                    transparentGroups.forEach((group) => {
                        // Find all path elements (ledger lines, stems, etc.) within this group
                        const paths = group.querySelectorAll('path');
                        paths.forEach((path) => {
                            // Make the path transparent
                            path.setAttribute('stroke', 'transparent');
                        });
                    });
                }
            }
        }

        // Calculate positions for fingering diagrams
        updateFingeringPositions(staveNotes);
    } catch (err) {
        console.error("VexFlow rendering error:", err);
        error.value = `Error rendering phrase: ${err instanceof Error ? err.message : "Unknown error"}`;
    }
}

function updateFingeringPositions(staveNotes: StaveNote[]) {
    const positions: typeof notesWithPositions.value = [];

    staveNotes.forEach((staveNote, index) => {
        const noteData = props.notes[index];
        const bbox = staveNote.getBoundingBox();

        // Get x position of note (center of the note head)
        const x = bbox.getX() + bbox.getW() / 2 - props.fingeringWidth / 2;

        // Convert MIDI to note name for display
        const preferFlat =
            noteData.spelling === "flat" ||
            (noteData.spelling === undefined && shouldPreferFlat(props.keySignature));
        const noteName = midiToNoteName(noteData.midi, preferFlat);

        // Get fingering for this note
        const fingering =
            noteData.fingering || getFingeringByMidi(noteData.midi, preferFlat) || {};

        // Determine if we should show this fingering
        const showFingering = shouldShowFingering(index, noteData.midi);

        positions.push({
            note: noteName,
            x,
            showFingering,
            fingering,
        });
    });

    notesWithPositions.value = positions;
}

// Computed height to accommodate fingerings
const containerHeight = computed(() => {
    if (!props.showFingerings) {
        return props.height;
    }
    return props.height + props.fingeringHeight + 20;
});

// Watch for prop changes and re-render
watch(
    [
        () => props.notes,
        () => props.clef,
        () => props.width,
        () => props.height,
        () => props.timeSignature,
        () => props.keySignature,
        () => props.enforceTimeSignature,
        () => props.overlay,
    ],
    () => {
        nextTick(() => {
            renderNotation();
        });
    },
    { deep: true },
);

onMounted(() => {
    nextTick(() => {
        renderNotation();
    });
});
</script>

<style scoped>
.music-phrase-container {
    display: inline-block;
    background: white;
    padding: 10px;
    border-radius: 8px;
}

.phrase-display {
    position: relative;
}

.vex-container {
    display: block;
}

.vex-container svg {
    border-radius: 3px;
}

.fingerings-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.fingering-wrapper {
    position: absolute;
    pointer-events: auto;
}

.error-message {
    color: #dc3545;
    font-size: 12px;
    text-align: center;
    padding: 5px;
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 3px;
    margin-top: 5px;
}
</style>
